
import { VideoGeneration } from '@/services/video/VideoGenerationService';
import { VideoGenerationOptions } from '@/modules/creative/video/schemas';
import { wrapTool, toolSuccess, toolError } from '../utils/ToolUtils';
import type { AnyToolFunction } from '../types';
import { performanceVideoService } from '@/services/video/PerformanceVideoService';
import { importWithRetry } from '@/utils/dynamicImport';
import { logger } from '@/utils/logger';
import type { MasterAudioReference } from '@/services/metadata/types';

// ============================================================================
// FIX #10: Input Validation Constants
// ============================================================================

const VALID_ASPECT_RATIOS = ['16:9', '9:16'] as const; // Veo 3.1 only supports these two
type ValidAspectRatio = typeof VALID_ASPECT_RATIOS[number];
const VALID_RESOLUTIONS = ['720p', '1080p', '4k'] as const;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ValidResolution = typeof VALID_RESOLUTIONS[number];
const MAX_DURATION_SECONDS = 300;
const MAX_CHAIN_DURATION_SECONDS = 300;

/**
 * Validates aspect ratio parameter
 */
function validateAspectRatio(aspectRatio?: string): string | null {
    if (!aspectRatio) return null;
    if (!(VALID_ASPECT_RATIOS as readonly string[]).includes(aspectRatio)) {
        return `Invalid aspect ratio "${aspectRatio}". Valid options: ${VALID_ASPECT_RATIOS.join(', ')}`;
    }
    return null;
}

/**
 * Validates resolution parameter
 */
function validateResolution(resolution?: string): string | null {
    if (!resolution) return null;
    if (!(VALID_RESOLUTIONS as readonly string[]).includes(resolution)) {
        return `Invalid resolution "${resolution}". Valid options: ${VALID_RESOLUTIONS.join(', ')}`;
    }
    return null;
}

/**
 * Validates duration parameter
 */
function validateDuration(duration: number | undefined, maxDuration: number = MAX_DURATION_SECONDS): string | null {
    if (duration === undefined) return null;
    if (typeof duration !== 'number' || isNaN(duration)) {
        return "Duration must be a valid number.";
    }
    if (duration <= 0) {
        return "Duration must be a positive number.";
    }
    if (duration > maxDuration) {
        return `Duration cannot exceed ${maxDuration} seconds.`;
    }
    return null;
}

// ============================================================================
// VideoTools Implementation
// ============================================================================

type VideoFirstFrameArgs = {
    prompt: string;
    image?: string;
    assetId?: string;
    recentImageIndex?: number;
    duration?: number;
    aspectRatio?: string;
    resolution?: string;
};

async function resolveFirstFrame(args: VideoFirstFrameArgs): Promise<{ firstFrame?: string; label?: string } | { error: ReturnType<typeof toolError> }> {
    if (args.image) return { firstFrame: args.image, label: 'provided image' };
    if (args.assetId === undefined && args.recentImageIndex === undefined) return {};

    const { useStore } = await importWithRetry(() => import('@/core/store'));
    const { generatedHistory, uploadedImages, userProfile } = useStore.getState();
    type AssetCandidate = { id?: string; url: string; type?: string; prompt?: string; description?: string };
    const rawItems: unknown[] = [
        ...(generatedHistory || []),
        ...(uploadedImages || []),
        ...(userProfile?.brandKit?.brandAssets || []),
        ...(userProfile?.brandKit?.referenceImages || []),
    ];
    const imageItems = rawItems.filter((item): item is AssetCandidate => (
        !!item
        && typeof item === 'object'
        && typeof (item as Record<string, unknown>)['url'] === 'string'
        && (
            (item as Record<string, unknown>)['type'] === undefined
            || (item as Record<string, unknown>)['type'] === 'image'
        )
    ));

    const selected = args.assetId !== undefined
        ? imageItems.find(item => item.id === args.assetId)
        : imageItems[args.recentImageIndex ?? -1];

    if (!selected?.url) {
        return {
            error: toolError(
                args.assetId !== undefined
                    ? `Image asset ${args.assetId} was not found in saved images, uploads, or brand assets.`
                    : `No saved image exists at recentImageIndex ${args.recentImageIndex}. Ask the user to choose one of the listed images.`,
                'IMAGE_ASSET_NOT_FOUND',
            ),
        };
    }

    return { firstFrame: selected.url, label: selected.id || selected.prompt || selected.description || 'recent image' };
}

export const VideoTools = {
    generate_video: wrapTool('generate_video', async (args: VideoFirstFrameArgs) => {
        // FIX #10: Comprehensive input validation
        if (!args.prompt || args.prompt.trim().length === 0) {
            return toolError("Prompt cannot be empty.", 'INVALID_INPUT');
        }

        const durationError = validateDuration(args.duration);
        if (durationError) {
            return toolError(durationError, 'INVALID_INPUT');
        }

        const aspectRatioError = validateAspectRatio(args.aspectRatio);
        if (aspectRatioError) {
            return toolError(aspectRatioError, 'INVALID_INPUT');
        }

        const resolutionError = validateResolution(args.resolution);
        if (resolutionError) {
            return toolError(resolutionError, 'INVALID_INPUT');
        }

        const resolvedFrame = await resolveFirstFrame(args);
        if ('error' in resolvedFrame) return resolvedFrame.error;

        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { userProfile, whiskState } = useStore.getState();

        // =====================================================================
        // WHISK INTEGRATION: Synthesize prompt with locked references
        // =====================================================================
        let finalPrompt = args.prompt;
        let finalAspectRatio: ValidAspectRatio | undefined = args.aspectRatio as ValidAspectRatio | undefined;
        let finalDuration = args.duration;

        // Check if we should use Whisk synthesis (video mode or both)
        if (whiskState && (whiskState.targetMedia === 'video' || whiskState.targetMedia === 'both')) {
            // Import WhiskService dynamically to avoid circular deps
            const { WhiskService } = await importWithRetry(() => import('@/services/WhiskService'));

            // Synthesize video-optimized prompt with Subject/Scene/Style/Motion
            finalPrompt = WhiskService.synthesizeVideoPrompt(args.prompt, whiskState);

            // Get video parameters from locked presets
            const videoParams = await WhiskService.getVideoParameters(whiskState);

            // Use locked aspect ratio if not explicitly provided
            if (!args.aspectRatio && videoParams.aspectRatio && (VALID_ASPECT_RATIOS as readonly string[]).includes(videoParams.aspectRatio)) {
                finalAspectRatio = videoParams.aspectRatio as ValidAspectRatio;
            }

            // Use locked duration if not explicitly provided
            if (!args.duration && videoParams.duration) {
                finalDuration = videoParams.duration;
            }
        }

        const { subscriptionService } = await importWithRetry(() => import('@/services/subscription/SubscriptionService'));
        const quotaCheck = await subscriptionService.canPerformAction('generateVideo', finalDuration || 4);
        if (!quotaCheck.allowed) {
            return toolError(`Quota exceeded: ${quotaCheck.reason || 'Insufficient funds or limits reached.'}`, 'QUOTA_EXCEEDED');
        }

        const results = await VideoGeneration.generateVideo({
            prompt: finalPrompt,
            firstFrame: resolvedFrame.firstFrame,
            duration: finalDuration,
            aspectRatio: finalAspectRatio,
            resolution: args.resolution as VideoGenerationOptions['resolution'],
            userProfile
        });

        if (results.length > 0) {
            const videoJob = results[0]!;

            // WAIT for job if URL is missing
            let finalUrl = videoJob.url;
            if (!finalUrl) {
                const completedJob = await VideoGeneration.waitForJob(videoJob.id);
                finalUrl = completedJob.videoUrl || '';
            }
            if (!finalUrl) {
                return toolError('Video generation completed without an output URL.', 'GENERATION_OUTPUT_MISSING');
            }

            const { addToHistory, currentProjectId } = useStore.getState();
            addToHistory({
                id: videoJob.id,
                url: finalUrl,
                prompt: args.prompt,
                type: 'video',
                timestamp: Date.now(),
                projectId: currentProjectId
            });

            return toolSuccess({
                id: videoJob.id,
                url: finalUrl,
                prompt: args.prompt,
                firstFrameSource: resolvedFrame.label
            }, `Video generated successfully${resolvedFrame.label ? ` from ${resolvedFrame.label}` : ''}: ${finalUrl}`);
        }
        return toolError('Video generation failed (no result returned).', 'GENERATION_FAILED');
    }),

    generate_motion_brush: wrapTool('generate_motion_brush', async (args: { image: string, mask: string, prompt?: string }) => {
        const { Video } = await importWithRetry(() => import('@/services/video/VideoService'));

        const imgMatch = args.image.match(/^data:(.+);base64,(.+)$/);
        const maskMatch = args.mask.match(/^data:(.+);base64,(.+)$/);

        if (!imgMatch || !maskMatch) {
            return toolError("Invalid image or mask data. Must be base64 data URIs.", 'INVALID_INPUT');
        }

        const image = { mimeType: imgMatch[1]!, data: imgMatch[2]! };
        const mask = { mimeType: maskMatch[1]!, data: maskMatch[2]! };

        const uri = await Video.generateMotionBrush(image, mask);

        if (uri) {
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const { addToHistory, currentProjectId } = useStore.getState();
            addToHistory({
                id: crypto.randomUUID(),
                url: uri,
                prompt: args.prompt || "Motion Brush",
                type: 'video',
                timestamp: Date.now(),
                projectId: currentProjectId
            });
            return toolSuccess({
                url: uri
            }, `Motion Brush video generated successfully: ${uri}`);
        }
        return toolError("Motion Brush generation failed.", 'GENERATION_FAILED');
    }),

    batch_edit_videos: wrapTool('batch_edit_videos', async (args: { prompt: string, videoIndices?: number[] }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { uploadedImages, addToHistory, currentProjectId } = useStore.getState();
        const allVideos = uploadedImages.filter(img => img.type === 'video');

        if (allVideos.length === 0) {
            return toolError("No videos found in uploads to edit. Please upload videos first.", 'NOT_FOUND');
        }

        const targetVideos = args.videoIndices
            ? args.videoIndices.map(i => allVideos[i]).filter(Boolean)
            : allVideos;

        if (targetVideos.length === 0) {
            return toolError("No valid videos found for the provided indices.", 'INVALID_INDEX');
        }

        const { remixVideo } = await importWithRetry(() => import('@/services/video/VideoRemixService'));
        const results = [];
        for (let index = 0; index < targetVideos.length; index += 1) {
            useStore.getState().addAgentMessage({
                id: crypto.randomUUID(),
                role: 'system',
                text: `Processing video ${index + 1} of ${targetVideos.length}...`,
                timestamp: Date.now()
            });
            const video = targetVideos[index]!;
            results.push(await remixVideo(video.storageUri || video.url, args.prompt));
        }

        if (results.length > 0) {
            results.forEach(res => {
                addToHistory({
                    id: res.id,
                    url: res.url,
                    prompt: res.prompt,
                    type: 'video',
                    timestamp: Date.now(),
                    projectId: currentProjectId
                });
            });
            return toolSuccess({
                processedCount: results.length,
                results
            }, `Successfully processed ${results.length} videos based on instruction: "${args.prompt}".`);
        }
        return toolError("Batch video processing completed but no videos were returned.", 'PROCESSING_FAILED');
    }),

    extend_video: wrapTool('extend_video', async (args: { videoUrl: string, prompt: string, direction: 'start' | 'end' }) => {
        const { extractVideoFrame } = await importWithRetry(() => import('@/utils/video'));
        const frameData = await extractVideoFrame(args.videoUrl);

        if (!frameData) {
            return toolError("Failed to extract frame from the provided video URL.", 'EXTRACTION_FAILED');
        }

        const options: VideoGenerationOptions = {
            prompt: args.prompt,
        };

        if (args.direction === 'start') {
            options.lastFrame = frameData;
        } else {
            options.firstFrame = frameData;
        }

        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { userProfile } = useStore.getState();
        options.userProfile = userProfile;

        const results = await VideoGeneration.generateVideo(options);

        if (results.length > 0) {
            const videoJob = results[0]!;

            let finalUrl = videoJob.url;
            if (!finalUrl) {
                const completedJob = await VideoGeneration.waitForJob(videoJob.id);
                finalUrl = completedJob.videoUrl || '';
            }
            if (!finalUrl) {
                return toolError('Video extension completed without an output URL.', 'GENERATION_OUTPUT_MISSING');
            }

            const { addToHistory, currentProjectId } = useStore.getState();
            addToHistory({
                id: videoJob.id,
                url: finalUrl,
                prompt: args.prompt,
                type: 'video',
                timestamp: Date.now(),
                projectId: currentProjectId
            });

            return toolSuccess({
                id: videoJob.id,
                url: finalUrl
            }, `Video extended successfully: ${finalUrl}`);
        }
        return toolError("Video extension failed (no result returned).", 'GENERATION_FAILED');
    }),

    update_keyframe: wrapTool('update_keyframe', async (args: { clipId: string, property: 'scale' | 'opacity' | 'x' | 'y' | 'rotation', frame: number, value: number, easing?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' }) => {
        const { useVideoEditorStore } = await importWithRetry(() => import('@/modules/creative/video/store/videoEditorStore'));
        const { updateKeyframe, addKeyframe, project } = useVideoEditorStore.getState();
        const clip = project.clips.find(c => c.id === args.clipId);

        if (!clip) {
            return toolError(`Clip with ID ${args.clipId} not found.`, 'NOT_FOUND');
        }

        addKeyframe(args.clipId, args.property, args.frame, args.value);

        if (args.easing) {
            updateKeyframe(args.clipId, args.property, args.frame, { easing: args.easing });
        }

        return toolSuccess({
            clipId: args.clipId,
            property: args.property,
            frame: args.frame,
            value: args.value,
            easing: args.easing
        }, `Keyframe updated for clip ${args.clipId} on property ${args.property} at frame ${args.frame} with value ${args.value}${args.easing ? ` and easing ${args.easing}` : ''}.`);
    }),

    generate_video_chain: wrapTool('generate_video_chain', async (args: { prompt: string, startImage: string, totalDuration: number, aspectRatio?: string }) => {
        // FIX #10: Comprehensive input validation
        if (!args.prompt || args.prompt.trim().length === 0) {
            return toolError("Prompt cannot be empty.", 'INVALID_INPUT');
        }

        const durationError = validateDuration(args.totalDuration, MAX_CHAIN_DURATION_SECONDS);
        if (durationError) {
            return toolError(durationError, 'INVALID_INPUT');
        }

        if (!args.totalDuration || args.totalDuration <= 0) {
            return toolError("Duration must be a positive number.", 'INVALID_INPUT');
        }

        const aspectRatioError = validateAspectRatio(args.aspectRatio);
        if (aspectRatioError) {
            return toolError(aspectRatioError, 'INVALID_INPUT');
        }

        const imgMatch = args.startImage.match(/^data:(.+);base64,(.+)$/);
        if (!imgMatch) {
            return toolError("Invalid startImage data. Must be a base64 data URI.", 'INVALID_INPUT');
        }

        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { userProfile, whiskState } = useStore.getState();

        // =====================================================================
        // WHISK INTEGRATION: Synthesize prompt with locked references
        // =====================================================================
        let finalPrompt = args.prompt;
        let finalAspectRatio: ValidAspectRatio | undefined = args.aspectRatio as ValidAspectRatio | undefined;
        let finalDuration = args.totalDuration;

        if (whiskState && (whiskState.targetMedia === 'video' || whiskState.targetMedia === 'both')) {
            const { WhiskService } = await importWithRetry(() => import('@/services/WhiskService'));
            finalPrompt = WhiskService.synthesizeVideoPrompt(args.prompt, whiskState);
            const videoParams = await WhiskService.getVideoParameters(whiskState);

            if (!args.aspectRatio && videoParams.aspectRatio && (VALID_ASPECT_RATIOS as readonly string[]).includes(videoParams.aspectRatio)) {
                finalAspectRatio = videoParams.aspectRatio as ValidAspectRatio;
            }

            if (!args.totalDuration && videoParams.duration) {
                finalDuration = videoParams.duration;
            }
        }

        useStore.getState().addAgentMessage({
            id: crypto.randomUUID(),
            role: 'system',
            text: `Queuing long-form background job for ${finalDuration}s...`,
            timestamp: Date.now()
        });

        const results = await VideoGeneration.generateLongFormVideo({
            prompt: finalPrompt,
            totalDuration: finalDuration,
            firstFrame: args.startImage,
            aspectRatio: finalAspectRatio,
            userProfile
        });

        if (results.length > 0) {
            const jobId = results[0]!.id;
            return toolSuccess({
                jobId
            }, `Long-form generation job started. Job ID: ${jobId}. You will see segments appear in your history as they are generated.`);
        }
        return toolError("Long-form video generation failed (no result returned).", 'GENERATION_FAILED');
    }),

    interpolate_sequence: wrapTool('interpolate_sequence', async (args: { firstFrame: string, lastFrame: string, prompt?: string }) => {
        const { useStore } = await importWithRetry(() => import('@/core/store'));
        const { userProfile } = useStore.getState();
        const results = await VideoGeneration.generateVideo({
            prompt: args.prompt || "Smooth transition between frames",
            firstFrame: args.firstFrame,
            lastFrame: args.lastFrame,
            userProfile
        });

        if (results.length > 0) {
            const videoJob = results[0]!;
            let finalUrl = videoJob.url;

            if (!finalUrl) {
                const completedJob = await VideoGeneration.waitForJob(videoJob.id);
                finalUrl = completedJob.videoUrl || '';
            }
            if (!finalUrl) {
                return toolError('Sequence interpolation completed without an output URL.', 'GENERATION_OUTPUT_MISSING');
            }

            const { addToHistory, currentProjectId } = useStore.getState();
            addToHistory({
                id: videoJob.id,
                url: finalUrl,
                prompt: args.prompt || "Frame Interpolation",
                type: 'video',
                timestamp: Date.now(),
                projectId: currentProjectId
            });

            return toolSuccess({
                id: videoJob.id,
                url: finalUrl
            }, `Sequence interpolated successfully: ${finalUrl}`);
        }
        return toolError("Interpolation failed (no result returned).", 'GENERATION_FAILED');
    }),

    orchestrate_video_render: wrapTool('orchestrate_video_render', async (args: { scriptTimeline: Array<{ sceneId: number, durationSeconds: number, description: string }> }) => {
        void args;
        return toolError('Video render orchestration is unavailable: no render queue backend is configured.', 'VIDEO_RENDER_QUEUE_UNAVAILABLE');
    }),

    orchestrate_timeline: wrapTool('orchestrate_timeline', async (args: { masterScript: string; totalDuration: number; artStyle: string }) => {
        void args;
        return toolError('Timeline orchestration is unavailable: no render queue backend is configured.', 'VIDEO_RENDER_QUEUE_UNAVAILABLE');
    }),

    generate_plp_variations: wrapTool('generate_plp_variations', async (args: { basePrompt: string }) => {
        void args;
        return toolError('Bulk video variation orchestration is unavailable: no A/B render queue backend is configured.', 'VIDEO_VARIATION_QUEUE_UNAVAILABLE');
    }),

    create_performance_video: wrapTool('create_performance_video', async (args: {
        masterAsset?: MasterAudioReference;
        artistImageUrl?: string;
        artistDescription?: string;
        style?: string;
        aspectRatio?: '9:16' | '16:9' | '1:1';
        sceneCount?: number;
    }) => {
        if (!args.masterAsset) {
            return toolError(
                'A verified canonical master must be selected before a performance video can be created.',
                'CANONICAL_MASTER_REQUIRED'
            );
        }

        if (!args.artistImageUrl && !args.artistDescription) {
            return toolError('Either artistImageUrl or artistDescription is required.', 'INVALID_INPUT');
        }

        try {
            const result = await performanceVideoService.generate({
                masterAsset: args.masterAsset,
                artistImageUrl: args.artistImageUrl,
                artistDescription: args.artistDescription,
                style: args.style,
                aspectRatio: args.aspectRatio,
                sceneCount: args.sceneCount,
            });
            if (!result.videoUrl) {
                return toolError('Performance video completed without an output URL.', 'GENERATION_OUTPUT_MISSING');
            }
            return toolSuccess(
                { videoUrl: result.videoUrl, projectId: result.projectId },
                `Performance video generated successfully: ${result.videoUrl}`
            );
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';
            return toolError(`Failed to generate performance video: ${errorMsg}`, 'PERFORMANCE_VIDEO_GENERATION_FAILED');
        }
    }),

    /**
     * Deterministic camera move over a still image (E1 - plan section 10).
     * Pure preset math through the shared render contract - no generative
     * model, no token cost.
     */
    animate_still: wrapTool('animate_still', async (args: {
        imageUrl: string;
        preset?: string;
        intensity?: number;
        durationSec?: number;
        resolution?: '9:16' | '16:9' | '4:5';
    }) => {
        if (!args.imageUrl) {
            return toolError('imageUrl is required (data URI or hosted still image).', 'INVALID_INPUT');
        }

        try {
            const { renderStillMotion, STILL_MOTION_RESOLUTIONS } = await importWithRetry(
                () => import('@/services/video/StillMotionRenderer')
            );
            const { MOTION_PRESETS, CINEMATIC_MOVE_PROMPTS } = await importWithRetry(
                () => import('@/services/video/MotionPresets')
            );

            const presetId = args.preset && MOTION_PRESETS[args.preset] ? args.preset : 'ken-burns';
            const resolution = args.resolution && args.resolution in STILL_MOTION_RESOLUTIONS ? args.resolution : '9:16';

            const receipt = await renderStillMotion({
                stillUrl: args.imageUrl,
                preset: presetId,
                intensity: args.intensity,
                durationSec: args.durationSec,
                resolution
            });

            const url = receipt.asset.url;
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const store = useStore.getState();
            const historyId = `motion_${Date.now()}`;
            store.addToHistory?.({
                id: historyId,
                url,
                type: 'video',
                prompt: `Animate still: ${presetId}`,
                timestamp: Date.now(),
                projectId: store.currentProjectId,
                meta: JSON.stringify({ source: 'motion_clip', preset: presetId, resolution }),
                tags: ['motion_clip', presetId],
                origin: 'generated'
            });

            // H1 producer hook - motion clips join the asset version graph.
            try {
                const { AssetVersionService } = await importWithRetry(() => import('@/services/assets/AssetVersionService'));
                await AssetVersionService.recordVersion({
                    assetId: historyId,
                    parentVersionId: null,
                    url,
                    source: 'canvas-export',
                    provenance: { note: `Deterministic motion clip (${presetId}); rendered from a still via the local render contract` },
                    tags: ['motion_clip', presetId]
                });
            } catch (versionError) {
                logger.warn('[VideoTools] Version record failed for motion clip; result unaffected:', versionError);
            }

            return toolSuccess({
                url,
                preset: presetId,
                resolution,
                deterministic: true,
                generativeNote: 'Deterministic camera move - no generative model was used. For model-generated motion (costs tokens), the generative micro-motion path is flag-gated off by default.',
                scaffoldIfEnabled: CINEMATIC_MOVE_PROMPTS[presetId as keyof typeof CINEMATIC_MOVE_PROMPTS]
            }, `Rendered deterministic ${presetId} motion clip at ${resolution}.`);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';
            logger.error('[VideoTools] animate_still failed:', err);
            return toolError(`Failed to animate still: ${errorMsg}`, 'MOTION_RENDER_FAILED');
        }
    })
} satisfies Record<string, AnyToolFunction>;

// Aliases
export const {
    generate_video,
    generate_motion_brush,
    batch_edit_videos,
    extend_video,
    update_keyframe,
    generate_video_chain,
    interpolate_sequence,
    orchestrate_video_render,
    orchestrate_timeline,
    generate_plp_variations,
    create_performance_video,
    animate_still
} = VideoTools;
